'use client';

import { useState } from 'react';
import { Study } from '@/lib/study';
import { T } from '@/lib/tokens';
import { extractJSON, run } from '@/lib/ai';
import { draftQuestions } from '@/lib/prompts';
import { Btn } from '../Btn';
import { Nav, StepHead, StepWrap } from '../StepShell';

type Props = {
  data: Study;
  setData: (d: Study) => void;
  onBack: () => void;
  onNext: () => void;
};

export function Step5DraftQuestions({ data, setData, onBack, onNext }: Props) {
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(data.questions.length > 0);
  const [skipped, setSkipped] = useState(false);
  const [numQuestions, setNumQuestions] = useState(5);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    setLoading(true);
    setError(null);
    try {
      const text = await run(draftQuestions(data, numQuestions));
      const qs = extractJSON<string[]>(text);
      if (qs) {
        setData({ ...data, questions: qs.map((q, i) => ({ id: i + 1, text: q })) });
        setGenerated(true);
      } else {
        setError('AI returned an unexpected response. Try again or skip.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'AI request failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StepWrap>
      <StepHead
        label="Step 6 — AI Draft"
        title="Let AI draft your questions"
        subtitle="Based on the information you've provided, the AI can generate a first draft of interview questions. You'll be able to edit them in the next step."
      />

      {!generated && !skipped && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div
            style={{
              background: T.white,
              border: `1.5px solid ${T.creamBorder}`,
              borderRadius: 14,
              padding: '28px 28px',
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
            }}
          >
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: T.terraLight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: 22,
                }}
              >
                ✦
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>Generate with AI</div>
                <div style={{ fontSize: 14, color: T.espressoMid, lineHeight: 1.6 }}>
                  The AI will read your study goal and aspects to produce semi-structured questions. This usually takes
                  about 10 seconds.
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <label style={{ fontSize: 14, fontWeight: 500, color: T.espresso, whiteSpace: 'nowrap' }}>
                Number of questions
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={() => setNumQuestions((n) => Math.max(1, n - 1))}
                  style={stepperBtn}
                  type="button"
                >
                  −
                </button>
                <span style={{ minWidth: 36, textAlign: 'center', fontSize: 18, fontWeight: 600, color: T.terra }}>
                  {numQuestions}
                </span>
                <button
                  onClick={() => setNumQuestions((n) => Math.min(20, n + 1))}
                  style={stepperBtn}
                  type="button"
                >
                  +
                </button>
              </div>
              <span style={{ fontSize: 13, color: T.espressoLight }}>(1–20)</span>
            </div>

            <Btn onClick={generate} disabled={loading}>
              {loading ? (
                <>
                  <span
                    style={{
                      display: 'inline-block',
                      animation: 'spin 1s linear infinite',
                    }}
                  >
                    ✦
                  </span>
                  Generating questions…
                </>
              ) : (
                'Generate Questions'
              )}
            </Btn>
            {loading && (
              <div
                style={{
                  fontSize: 13,
                  color: T.espressoLight,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  animation: 'pulse 1.6s ease-in-out infinite',
                }}
              >
                Asking Claude to draft your questions — this usually takes 5–10
                seconds.
              </div>
            )}
            {error && (
              <div style={{ fontSize: 13, color: T.danger }}>{error}</div>
            )}
          </div>

          <div style={{ textAlign: 'center', color: T.espressoLight, fontSize: 14 }}>— or —</div>

          <Btn
            variant="secondary"
            disabled={loading}
            onClick={() => {
              setSkipped(true);
              setData({
                ...data,
                questions: Array.from({ length: 5 }, (_, i) => ({ id: i + 1, text: '' })),
              });
            }}
          >
            Skip — I&apos;ll write my own questions
          </Btn>
        </div>
      )}

      {skipped && !generated && (
        <div
          style={{
            background: T.sageLight,
            border: `1px solid ${T.sage}`,
            borderRadius: 10,
            padding: '16px 18px',
            fontSize: 14,
            color: T.espressoMid,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <span>✓ You&apos;ll write your own questions in the next step.</span>
          <Btn small variant="secondary" onClick={() => setSkipped(false)}>
            ← Go back
          </Btn>
        </div>
      )}

      {generated && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              background: T.sageLight,
              border: `1px solid ${T.sage}`,
              borderRadius: 10,
              padding: '12px 16px',
              fontSize: 14,
              color: T.espressoMid,
            }}
          >
            ✓ {data.questions.length} questions generated. Review and edit them in the next step.
          </div>
          {data.questions.map((q, i) => (
            <div
              key={q.id}
              style={{
                background: T.white,
                border: `1.5px solid ${T.creamBorder}`,
                borderRadius: 10,
                padding: '14px 18px',
                display: 'flex',
                gap: 14,
              }}
            >
              <span style={{ color: T.terra, fontWeight: 600, minWidth: 24, paddingTop: 1 }}>{i + 1}.</span>
              <span style={{ fontSize: 15, lineHeight: 1.55 }}>{q.text}</span>
            </div>
          ))}
        </div>
      )}

      <Nav onBack={onBack} onNext={onNext} nextDisabled={!generated && !skipped} />
    </StepWrap>
  );
}

const stepperBtn: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: `1.5px solid ${T.creamBorder}`,
  background: T.cream,
  cursor: 'pointer',
  fontSize: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: T.espresso,
  fontFamily: 'inherit',
};
